import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link, Grid, Tooltip, IconButton, Button } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CalendarTodaySharpIcon from '@mui/icons-material/CalendarTodaySharp';
import ApartmentSharpIcon from '@mui/icons-material/ApartmentSharp';
import PersonSharpIcon from '@mui/icons-material/PersonSharp';
import AccessTimeSharpIcon from '@mui/icons-material/AccessTimeSharp';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useNavigate } from 'react-router-dom';
import { createNewDate, culture, options } from '../../Helpers/dates';
import StyledSwitch from '../../Components/Inputs/StyledSwitch';
import { FullSizeCenteredFlexBox } from '../../Components/FlexBox';
import { useAtomValue } from 'jotai';
import { themeAtom } from '../../Atoms';
import useAPI from '../../api';

const FiltersBreadCrumbs = function FiltersBreadCrumbs({
  currentFilters,
  toggle = () => {},
  navbar = true,
  hideBreadcrumbs = false,
  switchLabel = '',
  onSwitchClick = undefined,
  switchValue = false,
  isOperator = false,
  setCurrentFilters = () => {},
}) {
  const intl = useIntl();
  const API = useAPI();
  const themeMode = useAtomValue(themeAtom);
  const navigate = useNavigate();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [delay, setDelay] = useState(0);

  const handleSwitchClick = () => {
    if (onSwitchClick) onSwitchClick();
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh((prev) => !prev);
  };

  useEffect(() => {
    API.getSettingValue(
      'REPORT_REFRESH_INTERVAL',
      (res) => {
        setDelay(Number(res));
      },
      () => {
        setDelay(0);
      }
    );
  }, []);

  useEffect(() => {
    if (!delay || !autoRefresh) return;

    let timer = setTimeout(
      () => setCurrentFilters({ ...currentFilters, autoRefresh: true }),
      delay * 1000
    );

    return () => {
      clearTimeout(timer);
    };
  }, [autoRefresh, delay, currentFilters]);

  return (
    <Grid container sx={{ height: 'auto', width: '100%' }}>
      <Grid size={{ sm: 1, xs: 1 }}>
        <Tooltip
          placement="right"
          title={!navbar ? intl.messages.OPEN_NAV : intl.messages.CLOSE_NAV}
        >
          <IconButton onClick={toggle}>
            {navbar ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid size={{ sm: 9, xs: 9 }} sx={{ pl: '32px', pt: '16px', pb: '16px' }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" />}
        >
          <Button
            variant="text"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => {
              sessionStorage.setItem('currentSection', 'PRIORITIES');
              navigate('/priorities');
            }}
          >
            <HomeRoundedIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {intl.messages.BACK_HOME}
          </Button>
          {!hideBreadcrumbs && (
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              href="#navbar-filters"
              onClick={toggle}
            >
              <CalendarTodaySharpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {`${createNewDate(currentFilters?.startDate).toLocaleDateString(
                culture,
                options
              )} - ${createNewDate(currentFilters?.endDate).toLocaleDateString(
                culture,
                options
              )}`}
            </Link>
          )}
          {!hideBreadcrumbs && (
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              href="#navbar-filters"
              onClick={toggle}
            >
              <ApartmentSharpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {typeof currentFilters?.branches !== 'undefined'
                ? `${
                    currentFilters && currentFilters?.branches?.length === 0
                      ? intl.messages.ALL
                      : currentFilters?.branches?.length
                  } ${intl.messages.SELECTED}`
                : `0 ${intl.messages.SELECTED}`}
            </Link>
          )}
          {!hideBreadcrumbs && (
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              href="#navbar-filters"
              onClick={toggle}
            >
              <PersonSharpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {currentFilters?.operator?.label || intl.messages.NO_SELECTION}
            </Link>
          )}
          {!hideBreadcrumbs && (
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              href="#navbar-filters"
              onClick={toggle}
            >
              <AccessTimeSharpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {currentFilters?.grouping?.label || intl.messages.UNAVAILABLE}
            </Link>
          )}
        </Breadcrumbs>
      </Grid>
      <Grid size={{ sm: 2, xs: 1 }}>
        <FullSizeCenteredFlexBox gap="8px" justifyContent="flex-end">
          {!isOperator && (
            <StyledSwitch
              id="toggle-autoreload-report"
              label={intl.messages.AUTO_REFRESH}
              size="large"
              sx={{
                color: themeMode === 'dark' ? '#fff' : '#000',
                whiteSpace: 'nowrap',
              }}
              onChange={handleToggleAutoRefresh}
              checked={autoRefresh}
            />
          )}
          {switchLabel && onSwitchClick && (
            <StyledSwitch
              id="toggle-switch-report"
              label={switchLabel}
              size="large"
              sx={{
                color: themeMode === 'dark' ? '#fff' : '#000',
                whiteSpace: 'nowrap',
              }}
              onChange={handleSwitchClick}
              checked={switchValue}
            />
          )}
        </FullSizeCenteredFlexBox>
      </Grid>
    </Grid>
  );
};

FiltersBreadCrumbs.propTypes = {
  currentFilters: PropTypes.object.isRequired,
  toggle: PropTypes.func,
  navbar: PropTypes.bool,
};

export default FiltersBreadCrumbs;
